import {
  LookupCellData,
  UserEnteredLogicInputs,
  convertDefaultValueToCellData,
  isLookupCellData,
  isResolvedLookupCellData,
} from '@doss/common-all'
import { useFormikContext } from 'formik'
import { compact, first, get, isNil } from 'lodash'
import { CSSProperties, useCallback, useMemo, useState } from 'react'
import { Else, If, Then } from 'react-if'
import { Maybe } from 'yup'
import {
  DataType,
  TableColumn,
  useGetLookupColumnGroupQuery,
} from '~/common/generated/apollo-operations'
import { useDeviceType } from '~/common/hooks/useDeviceQuery'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import {
  AddRecordFormCell,
  LookupTableRow,
  LookupTableRowCell,
  RecordDetailTableCell,
  RecordDetailTableRow,
} from '~/components/records/types'
import { useDossSnackbar } from '~/components/shared/DossSnackbar'
import {
  RecordCellEditOrigin,
  useEditableTableCellOriginContext,
} from '~/components/table-cells/useEditableTableCellOriginProvider'
import { LookupCellValue } from '../add-record/AddRecordDialog'
import { useRecordDetailsCellPermissions } from '../RecordDetailsCellPermissionsProvider'
import { LookupProvider } from './hooks/LookupProvider'
import { LookupRowOptionsList } from './LookupRowOptionsList'
import { LookupRowSearch } from './LookupRowSearch'

function getNonPopoverContainerStyle(isMobile: boolean, columnWidth?: number): CSSProperties {
  return {
    width: columnWidth ? `${columnWidth}px` : undefined,
    minWidth: isMobile ? '100%' : '500px',
    maxHeight: isMobile ? '100%' : '750px',
    ...(isMobile && {
      height: '100%',
      overflow: 'hidden',
    }),
  }
}

export const LOOKUP_LIMIT = 50
export type LookupValue = LookupCellValue[] | LookupCellData
export type LookupState = LookupTableRow[] | LookupCellData | null

interface LookupCellProps {
  cell: RecordDetailTableCell | AddRecordFormCell
  lookupGroupId?: string
  showDirtyIndicator: boolean
  onCancel?: () => void
  columnWidth?: number
  parentRow?: Omit<RecordDetailTableRow, 'table' | 'rootWorkspace'>
  columns: Pick<TableColumn, 'id' | 'type' | 'innerType' | 'indexType'>[]
  selectedLookupRowIds: string[]
  row?: Pick<RecordDetailTableRow, 'cells'>
}

/** Owns the lookup edit draft; routes to inline picker (table details) or popover. */
export const LookupCell = ({
  cell,
  parentRow,
  lookupGroupId,
  showDirtyIndicator,
  onCancel,
  columnWidth,
  columns,
  selectedLookupRowIds,
  row,
}: LookupCellProps) => {
  const { t } = useTypedTranslation()
  const formik = useFormikContext<Record<string, Maybe<LookupValue>>>()
  const snackbar = useDossSnackbar()
  const { isReadOnly } = useRecordDetailsCellPermissions()
  const { isMobile } = useDeviceType()
  // Lookup state is either an array of selected lookup cells or a single lookup cell data value
  const [lookupState, setLookupState] = useState<LookupState>(null)
  const lookupCell = formik.values[cell.column.id]

  const lookupColumn = cell.column.lookupColumn
  const origin = useEditableTableCellOriginContext()
  const error = formik.errors[cell.column.id] as string | undefined

  const { data: lookupGroupData, loading: loadingLookupGroup } = useGetLookupColumnGroupQuery({
    variables: { lookupTableColumnGroupId: lookupGroupId ?? '' },
    skip: !lookupGroupId,
  })

  const lookupTableId = lookupGroupData?.lookupTableColumnGroup.lookupTable.id
  const lookupFilterExpressionId = cell.column.lookupFilterExpression?.id
  const lookupFilterExpressionString = cell.column.lookupFilterExpression?.logicExpressionString

  const isLookup = cell.column.type === DataType.LOOKUP
  const isArrayLookup =
    cell.column.type === DataType.ARRAY && cell.column.innerType === DataType.LOOKUP
  const hasLookupColumn = !isNil(lookupColumn)

  const values: LookupValue = useMemo(() => {
    if (!lookupCell) {
      return []
    }
    if (Array.isArray(lookupCell)) {
      return lookupCell
    }
    if (isLookupCellData(lookupCell)) {
      return lookupCell
    }
    return []
  }, [lookupCell])

  const tableDetailsUserEnteredValues = useMemo<UserEnteredLogicInputs | null>(() => {
    if (
      (!isLookup && !isArrayLookup) ||
      !hasLookupColumn ||
      origin !== RecordCellEditOrigin.TABLE_DETAILS
    ) {
      return null
    }

    if (!row) {
      throw new Error('Could not find row for cell update')
    }
    // When editing a lookup cell from the table details, the user entered values need to come
    // from the cell's row because formik will only contain the values for the cell's lookup
    // column group.
    return row.cells.reduce<UserEnteredLogicInputs>((acc, cell) => {
      const cellData = cell.data
      const columnId = cell.column.id
      if (!cellData) {
        acc[columnId] = {
          data: null,
          lookupCellIds: null,
          documentIds: null,
          userIds: null,
        }
        return acc
      }

      let cellValue = cell.data?.data
      // For multiple-user columns, we need to pull data from the `users` array, otherwise there
      // is no user id.
      if (cell.column.innerType === DataType.USER) {
        cellValue = cell.users
      }
      // For lookup columns, we need to pull data from the `lookupCells` array otherwise there is
      // no lookup cell id.
      if (cell.column.type === DataType.LOOKUP || cell.column.innerType === DataType.LOOKUP) {
        cellValue = cell.lookupCells
      }

      acc[columnId] = convertDefaultValueToCellData(
        cellValue,
        cell.column.type,
        cell.column.innerType,
        cell.column.indexType,
      )
      return acc
    }, {})
  }, [hasLookupColumn, isArrayLookup, isLookup, origin, row])

  const formUserEnteredValues = useMemo<UserEnteredLogicInputs | null>(() => {
    if (
      (!isLookup && !isArrayLookup) ||
      !hasLookupColumn ||
      origin === RecordCellEditOrigin.TABLE_DETAILS
    ) {
      return null
    }
    return Object.entries(formik.values).reduce<UserEnteredLogicInputs>(
      (acc, [columnId, value]) => {
        const column = columns.find((column) => column.id === columnId)

        if (!column) {
          throw new Error(`No column found for column ${columnId}`)
        }

        // Handle null values
        if (value === null || value === undefined) {
          acc[columnId] = { data: null, lookupCellIds: null, documentIds: null, userIds: null }
          return acc
        }

        acc[columnId] = convertDefaultValueToCellData(
          value,
          column.type,
          column.innerType,
          column.indexType,
        )
        return acc
      },
      {},
    )
  }, [columns, formik.values, hasLookupColumn, isArrayLookup, isLookup, origin])

  const userEnteredValues = tableDetailsUserEnteredValues ?? formUserEnteredValues ?? {}

  /**
   * When a new row has been selected, update every cell in the column group that is a lookup with
   * the new cell value.
   */
  const handleTableDetailsBulkChange = useCallback(
    (selection: LookupState) => {
      if (!lookupGroupData) {
        return
      }

      if (Array.isArray(selection)) {
        if (selection.length > 1 && !isArrayLookup) {
          snackbar.showError(t('record.lookup.cannot_select_multiple_rows'))
          return
        }

        const updateColumns = lookupGroupData.lookupTableColumnGroup.columns
        // Spread into new objects so formik.setValues receives a fresh reference. Mutating
        // formik.values in place keeps the reference stable, failing to trigger re-renders
        const updatedValues: Record<string, Maybe<LookupValue>> = {
          ...formik.values,
        }
        const updatedTouched: Record<string, boolean | undefined> = {
          ...formik.touched,
        }
        updateColumns.forEach((updateColumn) => {
          const updateLookupColumn = updateColumn.lookupColumn
          if (updateLookupColumn === null) {
            // If not a lookup column, skip
            return
          }
          // If the sibling cell in group is currently overriding the lookup, skip
          const parentRowCell = parentRow?.cells
            .filter((siblingCell) => 'id' in cell && siblingCell.id !== cell.id)
            .find((siblingCell) => siblingCell.column.id === updateColumn.id)

          if (parentRowCell) {
            // Check if the cell is overwriting the lookup
            const hasLookupCells = parentRowCell.lookupCells.length > 0
            const hasOverwrittenValue =
              !hasLookupCells &&
              isResolvedLookupCellData(parentRowCell.data) &&
              !isNil(parentRowCell.data.data)
            if (hasOverwrittenValue) {
              return
            }
          }

          // When adding a record, check if the value of the sibling cell is overriding the
          // lookup
          if (updateColumn.id !== cell.column.id) {
            const currentValue = get(updatedValues, updateColumn.id, undefined)
            if (!isNil(currentValue) && !Array.isArray(currentValue)) {
              return
            }
          }

          // Initialize an empty array to store the matched cells from the selected lookup rows.
          // For each selected row, we'll find the corresponding cell that matches the lookup column
          // and add it to this array. The result will either be used as-is for array lookups,
          // or we'll take just the first cell for single lookups.
          let newCells: LookupTableRowCell[] = []
          if (selection.length > 0) {
            newCells = compact(
              selection.map((row) => {
                const newCell = row.cells.find((cell) => cell.column.id === updateLookupColumn.id)
                return newCell ?? null
              }),
            )
          }
          const value = isArrayLookup ? newCells : compact([first(newCells)])
          if (value !== values) {
            updatedValues[updateColumn.id] = value
            if (showDirtyIndicator) {
              updatedTouched[updateColumn.id] = true
            }
          }
        })

        // Await each step: setValues' built-in validation runs against pre-update state, so we
        // disable it and re-validate after the value update has applied. Otherwise a stale
        // "required" error sticks on the field the user just filled in.
        void (async () => {
          await formik.setValues(updatedValues, false)
          await formik.setTouched(updatedTouched, false)
          await formik.validateForm()
        })()
      } else if (selection) {
        if (isArrayLookup) {
          throw new Error('Cannot unlink array of lookups')
        }
        if (selection !== values) {
          // If overwriting the lookup, do not update the other columns in the lookup column group
          formik.setFieldValue(cell.column.id, selection)
          if (showDirtyIndicator) {
            formik.setFieldTouched(cell.column.id, true)
          }
        }
      }
    },
    [
      lookupGroupData,
      isArrayLookup,
      formik,
      snackbar,
      parentRow?.cells,
      cell,
      values,
      showDirtyIndicator,
      t,
    ],
  )

  if ((!isLookup && !isArrayLookup) || !lookupColumn) {
    return null
  }

  return (
    <div className="h-full overflow-hidden">
      <div className="h-full overflow-hidden">
        <LookupProvider
          columns={lookupGroupData?.lookupTableColumnGroup.lookupTable.columns}
          isUnauth={false}
        >
          <If condition={origin === RecordCellEditOrigin.TABLE_DETAILS}>
            <Then>
              <div style={getNonPopoverContainerStyle(isMobile, columnWidth)}>
                <LookupRowOptionsList
                  column={lookupColumn}
                  error={error}
                  isArray={isArrayLookup}
                  isUnlinkedLookup={false}
                  loadingLookupGroup={loadingLookupGroup}
                  lookupColumnGroupLookupRowIds={selectedLookupRowIds}
                  lookupFilterExpressionId={cell.column.lookupFilterExpression?.id}
                  lookupFilterExpressionString={
                    cell.column.lookupFilterExpression?.logicExpressionString
                  }
                  lookupState={lookupState}
                  lookupTable={lookupGroupData?.lookupTableColumnGroup.lookupTable}
                  lookupTableId={lookupTableId ?? undefined}
                  onClose={onCancel}
                  parentRowId={parentRow?.id}
                  setLookupState={(newState) => {
                    setLookupState(newState)
                    handleTableDetailsBulkChange(newState)
                  }}
                  userEnteredValues={userEnteredValues}
                  values={values}
                />
              </div>
            </Then>
            <Else>
              <LookupRowSearch
                column={lookupColumn}
                disabled={isReadOnly}
                enableFilterSortRefinement
                error={error}
                getUserEnteredValues={() => userEnteredValues}
                isArray={isArrayLookup}
                isUnlinkedLookup={false}
                loadingLookupGroup={loadingLookupGroup}
                lookupColumnGroupLookupRowIds={selectedLookupRowIds}
                lookupFilterExpressionId={lookupFilterExpressionId}
                lookupFilterExpressionString={lookupFilterExpressionString}
                lookupState={lookupState}
                lookupTable={lookupGroupData?.lookupTableColumnGroup.lookupTable}
                onChange={handleTableDetailsBulkChange}
                parentRowId={parentRow?.id}
                setLookupState={setLookupState}
                values={values}
              />
            </Else>
          </If>
        </LookupProvider>
      </div>
    </div>
  )
}
